import React, { useCallback, useContext, useEffect } from 'react';
import { useHistory, useParams } from 'react-router-dom';

import CircularProgress from '@material-ui/core/CircularProgress';
import IconButton from '@material-ui/core/IconButton';
import AddIcon from '@material-ui/icons/Add';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import ArrowRightAltIcon from '@material-ui/icons/ArrowRightAlt';
import AutorenewIcon from '@material-ui/icons/Autorenew';
import DateRangeIcon from '@material-ui/icons/DateRange';
import DescriptionIcon from '@material-ui/icons/Description';
import PersonIcon from '@material-ui/icons/Person';
import cx from 'classnames';
import {
  LOGIN_URL,
  TEACHERS_PERMISSION_DENIED_URL,
  TEACHER_ASSIGNMENTS_V2_URL,
  TEACHER_ASSIGNMENT_V2_INSIGHTS_URL
} from 'config/urls';
import { TEACHER_ASSIGNMENT_CLASSROOM_INSIGHTS_URL } from 'config/urls';
import { isNil } from 'lodash';
import mixpanel from 'mixpanel-browser';
import moment from 'moment';
import { logout, useInsightsClassroomTrackerInstanceSectionDetail } from 'sdk';

import { ReactComponent as PaperIcon } from 'pages/Teachers/ClassroomInsights/assets/paper-icon.svg';
import { useNavbar } from 'pages/Teachers/shared/TeacherPageLayout';
import AccountMenuButton from 'pages/Teachers/shared/TeacherPageLayout/components/TopBar/components/AccountMenuButton';
import HelpButton from 'pages/Teachers/shared/TeacherPageLayout/components/TopBar/components/HelpButton';
import { colors } from 'theme/palette';
import { shutdownIntercomWidget } from 'utils/integrations/intercom';
import { trackMixpanelEvent } from 'utils/integrations/mixpanel';
import {
  MixpanelAction,
  MixpanelObject
} from 'utils/integrations/mixpanel/constants';
import { default as pusherManager } from 'utils/integrations/pusher';
import { reverse } from 'utils/urls';

import Button from 'components/Button';
import Typography from 'components/Typography';
import UserContext from 'components/UserContext';

import styles from './style.module.scss';

const Header = ({
  aboveCount,
  atCount,
  belowCount,
  needsReviewCount,
  loadingCount,
  regenerateDisabled,
  onRegenerateClick,
  onPlusButtonClick
}) => {
  const history = useHistory();

  const user = useContext(UserContext);
  const { navigation } = useNavbar({ title: '' });

  const logoutAndRedirect = async () => {
    shutdownIntercomWidget();
    pusherManager.disconnect();
    await logout().then(() => {
      history.push(LOGIN_URL);

      if (process.env.REACT_APP_MIXPANEL_KEY) {
        mixpanel.reset();
      }
    });
  };

  const { trackerInstanceSectionId } = useParams();
  const { data: sectionDetails, loading } =
    useInsightsClassroomTrackerInstanceSectionDetail({
      trackerInstanceSectionId
    });

  useEffect(() => {
    if (isNil(sectionDetails)) return;

    if (!sectionDetails.is_math_assignment) {
      history.push(TEACHERS_PERMISSION_DENIED_URL);
    }
  }, [sectionDetails, history]);

  const handleBackButtonClick = useCallback(() => {
    history.push(TEACHER_ASSIGNMENTS_V2_URL);
  }, [history]);

  const submitted =
    aboveCount + atCount + belowCount + needsReviewCount + loadingCount;

  const handleSwitchButtonClick = useCallback(() => {
    navigation?.setNoTopNavigation(false);
    history.push({
      pathname: reverse(TEACHER_ASSIGNMENT_V2_INSIGHTS_URL, {
        trackerInstanceSectionId
      })
    });

    trackMixpanelEvent(
      user.id,
      `${MixpanelObject.BUTTON} ${MixpanelAction.CLICK}`,
      {
        feature: 'live classroom insights',
        view: 'classroom insights header',
        button: 'switch to old insights experience',
        normalizedURL:
          process.env.REACT_APP_BASE_FRONTEND_URL +
          TEACHER_ASSIGNMENT_CLASSROOM_INSIGHTS_URL
      }
    );
  }, [history, navigation, trackerInstanceSectionId, user.id]);

  const handleRegenerateClick = useCallback(() => {
    onRegenerateClick();

    trackMixpanelEvent(
      user.id,
      `${MixpanelObject.BUTTON} ${MixpanelAction.CLICK}`,
      {
        feature: 'live classroom insights',
        view: 'classroom insights header',
        button: 'regenerate insights',
        normalizedURL:
          process.env.REACT_APP_BASE_FRONTEND_URL +
          TEACHER_ASSIGNMENT_CLASSROOM_INSIGHTS_URL
      }
    );
  }, [onRegenerateClick, user.id]);

  return (
    <div>
      <div className={styles.container}>
        <div className={styles.leftPart}>
          <IconButton onClick={handleBackButtonClick}>
            <ArrowBackIcon />
          </IconButton>
          <div className={styles.header}>
            {loading && <CircularProgress size={20} />}
            {!loading && (
              <>
                <Typography
                  variant="H-TEXT-1"
                  color={colors.blue1}
                  style={{ textTransform: 'none' }}
                >
                  {sectionDetails?.tracker_name}
                </Typography>

                <div className={styles.meta}>
                  <div>{sectionDetails?.section_name}</div>
                  <div>
                    <DescriptionIcon />
                    {sectionDetails?.pages_count} Pages
                  </div>
                  <div>
                    <DateRangeIcon />
                    {sectionDetails?.due_at &&
                      moment(sectionDetails?.due_at).format('MMM D, YYYY')}
                  </div>
                  <div>
                    <PersonIcon />
                    {sectionDetails?.students_count} students
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        <div className={styles.rightPart}>
          <Button
            endIcon={<ArrowRightAltIcon color="primary" />}
            fontSize="large"
            variant="primary"
            onClick={handleSwitchButtonClick}
            className={styles.switchButton}
          >
            Switch to old Insights Experience
          </Button>
          <div className={styles.actionsWrapper}>
            <HelpButton user={user?.user} />
            <AccountMenuButton user={user?.user} logout={logoutAndRedirect} />
          </div>
        </div>
      </div>
      <div className={styles.statuses}>
        <div className={styles.statusBox}>
          <b>{aboveCount}</b>{' '}
          <Typography variant="S-TEXT-1">Above Mastery</Typography>
        </div>
        <div className={cx(styles.statusBox, styles.blueBox)}>
          <b>{atCount}</b>{' '}
          <Typography variant="S-TEXT-1">At Mastery</Typography>
        </div>
        <div className={cx(styles.statusBox, styles.yellowBox)}>
          <b>{belowCount}</b>
          <Typography variant="S-TEXT-1">Below Mastery</Typography>
        </div>
        <div className={styles.reviewState}>
          <Typography variant="S-TEXT-1">
            ({needsReviewCount}) Needs Review
          </Typography>
        </div>
        <div className={styles.studentUploads}>
          <PaperIcon />
          <Typography variant="S-TEXT-1">
            {submitted}/{sectionDetails?.students_count} Uploads
          </Typography>
        </div>
        <div className={styles.buttons}>
          <IconButton className={styles.addButton} onClick={onPlusButtonClick}>
            <AddIcon fontSize="large" />
          </IconButton>
          <Button
            startIcon={<AutorenewIcon color="primary" />}
            disabled={regenerateDisabled}
            className={styles.regenerateInsights}
            onClick={handleRegenerateClick}
          >
            Regenerate Insights
          </Button>
        </div>
      </div>
    </div>
  );
};

export default Header;
